import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import type { SkillTag } from "../../../../../client";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../../components";
import EditorSkillTagButton from "../../../../../components/atoms/EditorSkillTagButton";

interface Props {
  skillTag: SkillTag;
  hideIcon?: boolean;
  pdfMode?: boolean;
  /** When provided, the chip becomes removable — click opens a "Ta bort" popover. */
  onRemove?: (skillName: string) => void;
}

/**
 * A skill chip that is inline-removable in the template editor. Without
 * `onRemove` it renders a plain, static chip (identical to the previous inline
 * EditorSkillTagButton usage), so the /pdf and public views are unchanged.
 */
export const EditableSkillTag = ({
  skillTag,
  hideIcon,
  pdfMode,
  onRemove,
}: Props) => {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const scheduleClose = (ms: number) => {
    clearTimer();
    timerRef.current = setTimeout(() => setOpen(false), ms);
  };

  // Auto-dismiss so a forgotten "Ta bort" popover doesn't linger over the CV:
  // 3s from when it appears if the pointer never enters it. Hovering pauses the
  // countdown (onMouseEnter clears it); leaving restarts it at 2s. Radix also
  // closes it on an outside click / Escape.
  useEffect(() => {
    if (!open) return;
    timerRef.current = setTimeout(() => setOpen(false), 3000);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open]);

  const chip = (
    <EditorSkillTagButton
      profileSkillTag={skillTag}
      text={skillTag.name}
      disableEditing={true}
      isStatic={true}
      keepLeftPadding={true}
      hideIcon={hideIcon ?? false}
      displayInfoOnHover={!onRemove}
      pdfMode={pdfMode}
      handleClick={() => {}}
    />
  );

  // `data-skill-chip` marks each chip so a layout can clip the skill list to
  // whole rows (see SplitTemplate's left column) — the marker is inert otherwise.
  if (!onRemove)
    return (
      <span data-skill-chip className="inline-flex">
        {chip}
      </span>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div data-skill-chip className="inline-flex cursor-pointer">
          {chip}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="z-[9999] w-auto rounded-lg p-1"
        sideOffset={6}
        onMouseEnter={clearTimer}
        onMouseLeave={() => scheduleClose(2000)}
      >
        <button
          type="button"
          className="flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-op-red hover:bg-red-1"
          onClick={() => {
            onRemove(skillTag.name);
            setOpen(false);
          }}
        >
          <FontAwesomeIcon
            width={13}
            height={13}
            icon={faTrashCan}
            className="shrink-0"
          />
          Remove
        </button>
      </PopoverContent>
    </Popover>
  );
};
